/**
 * m3 echo Frontend - Model Catalog React Query Hooks
 *
 * Lesbare Modellnamen und die aktuelle Benchmark-Aufstellung.
 */

import { useQuery, type QueryClient } from '@tanstack/react-query';
import { settingsApi } from '../services/api';
import { getIdentityScope } from '../stores/useAuthStore';
import type { AIModel, BenchmarkModelInfo } from '../types/api';

const STALE_TIME = 5 * 60 * 1000;

export const modelCatalogKeys = {
  allCatalog: ['model-catalog'] as const,
  allBenchmark: ['benchmark-models'] as const,
  catalog: () => [...modelCatalogKeys.allCatalog, getIdentityScope()] as const,
  benchmark: (tenantId?: number) =>
    [...modelCatalogKeys.allBenchmark, getIdentityScope(), tenantId ?? null] as const,
};

/**
 * Verwirft Modellkatalog und Benchmark-Aufstellung im Cache (nach Admin-Änderungen).
 */
export const invalidateModelCatalog = (queryClient: QueryClient) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: modelCatalogKeys.allCatalog }),
    queryClient.invalidateQueries({ queryKey: modelCatalogKeys.allBenchmark }),
  ]);

/**
 * Liefert `modelLabel(code)`: display_name des Modells, sonst der Code selbst.
 */
export const useModelCatalog = () => {
  const query = useQuery<AIModel[]>({
    queryKey: modelCatalogKeys.catalog(),
    queryFn: async () => (await settingsApi.getAIModels()).data,
    staleTime: STALE_TIME,
  });

  const modelLabel = (code: string): string =>
    query.data?.find((m) => m.model_code === code)?.display_name || code;

  return { models: query.data ?? [], modelLabel, isLoading: query.isLoading };
};

/**
 * Aktuelle zentral festgelegte Benchmark-Modelle eines Mandanten.
 * Ohne `tenantId` gilt der eigene Mandant; Admins können einen Zielmandanten angeben.
 */
export const useCurrentBenchmarkModels = (tenantId?: number, options?: { enabled?: boolean }) =>
  useQuery<BenchmarkModelInfo[]>({
    queryKey: modelCatalogKeys.benchmark(tenantId),
    queryFn: async () => (await settingsApi.getCurrentBenchmarkModels(tenantId)).data,
    staleTime: STALE_TIME,
    enabled: options?.enabled ?? true,
  });
